import React from 'react';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type {
  SubscriptionBillingInterval,
  SubscriptionBillingIntervalResponse,
} from 'types/entityTypes/SubscriptionBillingInterval';
import type { DropdownOption } from 'types/common/DropdownOption';

const getSubscriptionBillingIntervals = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.SUBSCRIPTION_BILLING_INTERVALS);
  return fetchWrapper<SubscriptionBillingIntervalResponse>({
    url: url,
    method: 'GET',
  });
};

export function useSubscriptionBillingIntervals() {
  const response = useQuery(
    'subscriptionBillingIntervals',
    getSubscriptionBillingIntervals,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to fetch subscription billing intervals: ${error.message}`
          );
        }
      },
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: subscriptionBillingIntervals } = response.data || {};

  const options: DropdownOption[] | undefined = React.useMemo(() => {
    return subscriptionBillingIntervals?.map((interval) => {
      let label = interval.attributes.name;

      if (label === 'Month') {
        label = 'Monthly';
      } else if (label === 'Year') {
        label = 'Yearly SAVE 20%';
      }

      return {
        value: interval.id,
        label,
      };
    });
  }, [subscriptionBillingIntervals]);

  const intervalNameMap = React.useMemo(() => {
    return subscriptionBillingIntervals?.reduce<
      Record<string, SubscriptionBillingInterval>
    >((acc, curr) => {
      return { ...acc, [curr.attributes.name]: curr };
    }, {});
  }, [subscriptionBillingIntervals]);

  return {
    subscriptionBillingIntervals,
    options,
    intervalNameMap,
    ...response,
  };
}
